import { AG_GRID_LOCALE_DE } from '@ag-grid-community/locale';
import { Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { CellEditorSelectorResult, CellKeyDownEvent, CellPosition, CellRendererSelectorResult, ColDef, Column, GetRowIdFunc, GetRowIdParams, GridReadyEvent, ICellEditorParams, ICellRendererParams, IRowNode, RowDragEndEvent } from "ag-grid-community";
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { ExcelColumn } from 'src/modules/enaio/shared/ExcelColumn';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendFieldDefinition } from '../../models/frontend-field-definition.model';
import { FrontendFieldType } from '../../models/frontend-field-type.enum';
import { FrontendFormDefinition } from '../../models/frontend-form-definition.model';
import { TableCellButton } from '../../models/table-cell-button.model';
import { TableCellEvent } from '../../models/table-cell-event.model';
import { TableCellType } from '../../models/table-cell-type.enum';
import { TableCell } from '../../models/table-cell.model';
import { TableColumn } from '../../models/table-column.model';
import { TableData } from '../../models/table-data.model';
import { TableRow } from '../../models/table-row.model';
import { SmTableCellEditorComponent } from '../sm-table-cell-editor/sm-table-cell-editor.component';
import { SmTableCell2Component } from '../sm-table-cell2/sm-table-cell2.component';
import { SmTableHeaderComponent } from '../sm-table-header/sm-table-header.component';
import { Config } from 'src/modules/app-template/models/config.model';

@Component({
  selector: 'sm-table2',
  templateUrl: './sm-table2.component.html',
  styleUrl: './sm-table2.component.scss'
})
export class SmTable2Component {

    static defaultUseTableMenu = true;
    static defaultUseColumnMenu = true;
    static defaultActionButtons = false;
    static defaultFilterButtons = true;
    static convertCsvToExcel: (csv: string, columns: ExcelColumn[]) => Promise<string>;

    static COLUMN_ID_REMOVE = "___???specialDelete???___";
    static COLUMN_ID_EDIT_ROW_FULL_SCREEN = "___???specialEditRowFullScreen???___";

    static locale: any = AG_GRID_LOCALE_DE;

    static setLanguage(lng: string) {
        if (lng == "de") {
            AG_GRID_LOCALE_DE.noRowsToShow = "Keine Daten vorhanden";
        }
        SmTable2Component.locale = lng == "de" ? AG_GRID_LOCALE_DE : {};
    }

    _SmTable2Component = SmTable2Component;

    app = inject(MainAppService);

    @Input()
    compact = true;
    @Input()
    tableSizer = true;
    @Input()
    columnWidthsByContent = false;
    @Input()
    horizontalScroll = false;
    @Input()
    allowFilter = true;
    @Input()
    floatingFilter = false;
    @Input()
    autoHeight = false;
    @Input()
    tabForSameRow = false;
    @Input()
    displayRowNumbers = false;
    @Input()
    displaySideBar = true;
    @Input()
    allowAddRow = false;
    _allowRemoveRow = false;
    @Input()
    removeRowWarningMessage: string = null;
    @Input()
    allowCellTextSelection = true;
    _allowRowDrag = false;
    @Input()
    headerHeight = null;
    @Input()
    alowSorting = true;
    @Input()
    addRowOnTabInLastCell = false;
    _fullScreenRowEditor = false;
    _flashChanges = false;
    @Input()
    multiSelection = false;
    @Input()
    editOnClick = true;
    @Input()
    disabled = false;
    _readOnly = false;
    _autoRowHeight = false;
    @Input()
    invalidCellStyleClass = "xbg-red-100";
    _checkBoxSelection = false;
    @Input()
    performAddTableRow: () => void = null;
    @Input()
    performRemoveTableRow: (row: TableRow, rowIndex: number) => void = null;
    @Input()
    performSetTableRows: (rows: TableRow[]) => void = null;
    @Input()
    mustRefresh: () => boolean = null;
    @Input()
    useTableMenu = SmTable2Component.defaultUseTableMenu;
    @Input()
    useColumnMenu = SmTable2Component.defaultUseColumnMenu;
    @Input()
    actionButtons = SmTable2Component.defaultActionButtons;
    _data: TableData;
    //eigentlich richtigerweise: colDefs: (ColDef<any> | ColGroupDef<any>)[];
    colDefs: ColDef[];
    context: OrdinaryObject = {};
    autoSizeStrategy: any = {
        type: "fitGridWidth"
    };
    pinnedBottomRows: any[] = [];

    components: {[p: string]: any;} = {
        agColumnHeader: SmTableHeaderComponent
    };

    isEnterprise = !Utils.isNoe(Config.get().agGridEnterpriseKey);

    _selection: any;
    @Output()
    selectionChange = new EventEmitter<any>();


    @Output()
    onRowSelect = new EventEmitter<TableRow>();
    @Output()
    onRowDoubleClicked = new EventEmitter<TableRow>();
    @Output()
    onCellClicked = new EventEmitter<TableCellEvent>();
    @Output()
    onCellValueChanged = new EventEmitter<TableCellEvent>();
    @Output()
    onCellEditingStarted = new EventEmitter<TableCellEvent>();
    @Output()
    onCellEditingStopped = new EventEmitter<TableCellEvent>();
    @Output()
    onCellFocused = new EventEmitter<TableCellEvent>();
   @ViewChild("grid") grid: AgGridAngular;
  
    fullRowEditForm: FrontendFormDefinition;
    fullRowEditRowNode: IRowNode;

    tabToNextCellBound = this.tabToNextCell.bind(this);

    @Input()
    set checkBoxSelection(value: boolean) {        
        this._checkBoxSelection = value;
        if (this.colDefs != null) {
            for (let [index, colDef] of this.colDefs.entries()) {
                colDef.checkboxSelection = value && index == 0;
            }
        }
    }    

    @Input()
    set flashChanges(value: boolean) {
        this._flashChanges = value;
        if (this.colDefs != null) {
            for (let colDef of this.colDefs) {
                colDef.enableCellChangeFlash = value;
            }
        }
    }    

    @Input()
    set fullScreenRowEditor(value: boolean) {
        this._fullScreenRowEditor = value;
        if (this.colDefs != null) {
            let indexOfCol = this.colDefs.findIndex(colDef => colDef.context.specialColumn == "editRowFullScreen");
            if (!this.isFullScreenRowEditorColumnVisible() && indexOfCol >= 0) {
                this.colDefs.splice(indexOfCol, 1);
            }            
            if (this.isFullScreenRowEditorColumnVisible() && indexOfCol < 0) {
                this.colDefs = [...this.colDefs, this.getFullScreenRowEditColDef()];
                this.updateColumnWidthsForMobile();
            }
        }
    }   

    updateColumnWidthsForMobile(): void {
        this.grid != null ? this.updateColumnWidthsForMobileInternal() : Utils.setTimerOnce(100, this.updateColumnWidthsForMobileInternal.bind(this));
    }

    updateColumnWidthsForMobileInternal(): void {
        this.grid.api.setColumnsVisible(this._data.columns.slice(2).map(col => col.id), false);
        this.grid.api.sizeColumnsToFit();
    }

    updateRemoveColumn(): void {
        if (this.colDefs != null) {
            let indexOfRemoveCol = this.colDefs.findIndex(colDef => colDef.context.specialColumn == "remove");
            if ((!this._allowRemoveRow || this._readOnly) && indexOfRemoveCol >= 0) {
                this.colDefs.splice(indexOfRemoveCol, 1);
            }            
            if (this._allowRemoveRow && !this._readOnly && indexOfRemoveCol < 0) {
                this.colDefs.push(this.getRemoveColDef());
            }
        }
    }

    @Input()
    set allowRemoveRow(value: boolean) {
        this._allowRemoveRow = value;
        this.updateRemoveColumn();        
    }

    @Input()
    set allowRowDrag(value: boolean) {
        this._allowRowDrag = value;
        if (this.colDefs != null) {
            let i = 0;
            for (let colDef of this.colDefs) {
                colDef.rowDrag = this._allowRowDrag && i++ == 0;
            }
        }
    }

    @Input()
    set autoRowHeight(value: boolean) {
        this._autoRowHeight = value;
        if (this.colDefs != null) {
            for (let colDef of this.colDefs) {
                colDef.autoHeight = value;
            }
        }
    }

    @Input()
    set readOnly(value: boolean) {
        this._readOnly = value;
        this.updateRemoveColumn();
    }

    private getRemoveColDef(): ColDef {
        return {
            colId: SmTable2Component.COLUMN_ID_REMOVE,
            context: {
                specialColumn: "remove",
                table: this,
                tableColumn: new TableColumn(SmTable2Component.COLUMN_ID_REMOVE, "", TableCellType.button, { autoWidth: 1 })
            },
            width: 30,
            cellRenderer: SmTableCell2Component,
            cellRendererParams: {
                table: this
            },
            valueGetter: params => (params.data as TableRow).canDelete() ? [new TableCellButton("remove", "fas fa-trash", "Löschen")] : []
        }
    }

    private getFullScreenRowEditColDef(): ColDef {
        return {
            colId: SmTable2Component.COLUMN_ID_EDIT_ROW_FULL_SCREEN,
            context: {
                specialColumn: "editRowFullScreen",
                table: this,
                tableColumn: new TableColumn(SmTable2Component.COLUMN_ID_EDIT_ROW_FULL_SCREEN, "", TableCellType.button, { autoWidth: 1 })
            },
            width: 30,
            cellRenderer: SmTableCell2Component,
            cellRendererParams: {
                table: this
            },
            pinned: "left",
            valueGetter: params => params.node.rowPinned != 'bottom' ? [new TableCellButton("editRow", "fas fa-pencil", "Zeile editieren")] : []
        }
    }

    sortCache = new Map<any, string>();

    @Input()
    set data(value: TableData) {
        let columnsChanged = !Utils.arrayEqualsFlat(value?.columns, this._data?.columns);
        this._data = value;
        this.context = {
            table: this
        };
        if (this._data != null && columnsChanged) {
            this.colDefs = [];
            let colIdx = 0;
            for (let col of this._data.columns) {
                let sortIndex = this._data.sort?.findIndex(sc => sc.id == col.id);
                let widthFlex = this.tableSizer ? col.getRealWidth(this._data, this.columnWidthsByContent) : null;
                let colDef: ColDef = {
                    field: col.id,
                    colId: col.id,
                    cellDataType: col.cellType == TableCellType.yesNo ? "boolean" : col.cellType == TableCellType.int || col.cellType == TableCellType.decimal ? "number" : col.cellType == TableCellType.date || col.cellType == TableCellType.dateTime ? "date" : "text",
                    valueGetter: params => params.node.group ? null : params.data.values[col.id] == "_???addRow???_" ? params.data.values[col.id] : col.getValue(TableCell.getValue(params.data.values[col.id])),
                    valueFormatter: params => {
                        try {
                            return params.value == "_???addRow???_" ? params.value : col.formatValue(params.value, this.app.language);
                        }
                        catch (error) {
                            console.log("Der Wert " + Utils.toString(params.value) + " für die Spalte " + col.id + " konnte nicht formatiert werden");
                            return "<???>";
                        }
                    },
                    checkboxSelection: this.checkBoxSelection,
                    context: {       
                        specialColumn: null,                 
                        table: this,
                        tableColumn: col
                    },
                    suppressMovable: true,
                    editable: params => this.isCellEditableInternal(params.node, col),
                    valueSetter: col.editable ? params => {
                        let colx = this.getColDefAndIndex(col.id);
                        col.setValue(new TableCellEvent(params.newValue, params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index));
                        return true;
                    } : null,
                    cellEditorSelector: params => this.getCellEditor(params, col),
                    //singleClickEdit: true,
                    filter: true,
                    floatingFilter: this.floatingFilter,
                    headerName: col.caption,
                    //wrapHeaderText: true,
                    autoHeaderHeight: true,
                    //headerCheckboxSelection: true,
                    pinned: col.frozen,
                    cellClass: params => {
                        if (params.node.group) {
                            return null;
                        }
                        if (params.data.values[col.id] instanceof TableCell) {
                            let result = params.data.values[col.id].styleClass;
                            if (result != null) {
                                return result;
                            }
                         }
                         if (col.getStyleClassCallback != null && params.node.rowPinned != 'bottom') {
                            let colx = this.getColDefAndIndex(col.id);
                            return col.getStyleClassCallback(new TableCellEvent(TableCell.getValue(params.data.values[col.id]), params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index));
                        }
                        else {
                            return null;
                        }
                    },
                    cellRendererSelector: params => this.getCellRenderer(params, col),
                    cellRenderer: col.cellRenderer,
                    enableCellChangeFlash: this._flashChanges,
                    rowDrag: this._allowRowDrag && colIdx == 0, 
                    enableRowGroup: this.isEnterprise,
                    enableValue: this.isEnterprise,
                    enablePivot: this.isEnterprise,
                    sortable: col.sortable,
                    sort: sortIndex == -1 || sortIndex == null ? null : this._data.sort[sortIndex].ascending ? "asc" : "desc",
                    sortIndex: sortIndex != -1 ? sortIndex : null,
                    comparator: col.cellType != TableCellType.text ? null : (a, b) => {
                        const getCmp = (value: any): string => {
                            (window as any).cmpCount = ((window as any).cmpCount ?? 0) + 1;
                            let cached = this.sortCache.get(value);
                            if (cached == undefined) {
                                (window as any).cmpCountCached = ((window as any).cmpCountCached ?? 0) + 1;
                                cached = Utils.toString(value).normalize('NFD').toLowerCase();
                                this.sortCache.set(value, cached);
                            }                          
                            return cached;  
                        };
                      
                        let cmp1 = getCmp(a);
                        let cmp2 = getCmp(b);

                        return cmp1 < cmp2 ? -1 : cmp1 > cmp2 ? 1 : 0;
                      },
                    colSpan: params => params.data == null ? 1 : params.data.values[col.id] == "_???addRow???_" ? this._data.columns.length + (this._allowRemoveRow ? 1 : 0) : params.data.values[col.id] instanceof TableCell ? params.data.values[col.id].colSpan : 1,
                    tooltipValueGetter: params => {
                        if (col.toolTipGetter != null && params.node.rowPinned != 'bottom') {
                            let colx = this.getColDefAndIndex(col.id);
                            return colx == null ? null : col.toolTipGetter(new TableCellEvent(TableCell.getValue(params.data.values[col.id]), params.node.data as TableRow, colx.def, params.node.rowIndex, colx.index));
                        }
                         else {
                            return null;
                         }
                    },
                    width: widthFlex?.width,
                    flex: widthFlex?.flex,                    
                    resizable: col.resizable,
                    autoHeight: this._autoRowHeight,
                };
                this.colDefs.push(colDef);
                colIdx++;
            }
            if (this._allowRemoveRow && !this._readOnly) {
                this.colDefs.push(this.getRemoveColDef());
            }
            if (this.isFullScreenRowEditorColumnVisible() && this.app.isMobile()) {
                this.colDefs.push(this.getFullScreenRowEditColDef());
            }

            let delCols: number[] = [];
            let groupCaptions = Utils.arrayGetUnique(this._data.columns.filter(col => !Utils.isNoe(col.groupCaption)).map(col => col.groupCaption));
            for (let groupCaption of groupCaptions) {
                let groupColumn = null;
                let colIdx = 0;
                for (let col of this._data.columns) {
                    if (col.groupCaption == groupCaption) {
                        if (groupColumn == null) {
                                groupColumn = {
                                headerName: groupCaption,
                                children: [ 
                                    this.colDefs[colIdx] 
                                ]
                            }
                            this.colDefs[colIdx] = groupColumn;
                        }
                        else {
                            groupColumn.children.push(this.colDefs[colIdx]);
                            delCols.push(colIdx);
                        }
                    }
                    colIdx++;
                }
            }

            for (let delCol of Utils.arrayReverse(Utils.arraySort(delCols))) {
                this.colDefs.splice(delCol, 1);
            }
            
            this.pinnedBottomRows = [new TableRow("", {}, Utils.arrayConvertToOrdinaryObject(this.colDefs, col => col.colId, col => col.colId == this.colDefs[0].colId ? "_???addRow???_" : ""))];

            if (this.isFullScreenRowEditorColumnVisible()) {
                this.updateColumnWidthsForMobile();
            }
        }
    }
/*
    private string getCellStyleClass(node: IRowNode, column: TableColumn): string {
        if (node.data.values[column.id] instanceof TableCell && node.data.values[col.id].styleClass) {
            
        }
        } ? params.data.values[col.id].styleClass : col.getStyleClassCallback != null && params.node.rowPinned != 'bottom' ? 
                        col.getStyleClassCallback(TableCell.getValue(params.data.values[col.id]), params.node.rowIndex, this.getColIndex(col.id)) : null
    }

  */  
 
    private isCellEditableInternal(rowNode: IRowNode, column: TableColumn): boolean {
        if (this._readOnly || rowNode.rowPinned == "bottom") {
            return false;
        }
        let col = this.getColDefAndIndex(column.id);
        return column.isEditable(new TableCellEvent(TableCell.getValue(rowNode.data.values[column.id]), rowNode.data as TableRow, col.def, rowNode.rowIndex, col.index));
    }

    private getCellRenderer(params: ICellRendererParams, column: TableColumn): CellRendererSelectorResult {
        if  (params.node.group) {
            return null;
        }
        if (params.data.values[column.id] == "_???addRow???_" && column.id == this.colDefs[0].colId) {
            // Plus-Button in der Add-Zeile
            return { component: SmTableCell2Component };
        }
        if (column.cellType == TableCellType.button) {
            return { component: SmTableCell2Component };
        }
        if ((params.node as any).hover && column.cellType != TableCellType.yesNo) {
            //Mouse-Hover
            return { component: SmTableCell2Component };
        }
        if (params.data.values[column.id] instanceof TableCell && params.data.values[column.id].keepHtml) {
            //HTML beibehalten
            return { component: SmTableCell2Component };
        }
        return null;
    }
    
    private getCellEditor(params: ICellEditorParams<any, any, any>, column: TableColumn): CellEditorSelectorResult {
        if (!column.editable || params.node.rowPinned == "bottom") {
            return undefined;
        }
        if (column.editor != null) {
            return {
                component: SmTableCellEditorComponent,
                params: {
                    editor: column.getEditor(() => new TableCellEvent(TableCell.getValue(params.data.values[column.id]), params.data, this.colDefs.find(colDef => colDef.colId == column.id), params.node.rowIndex, this.getColIndex(column.id)))                       
                }
            }
        }        
        if (column.cellType == TableCellType.text) {
            return {
                component: "agTextCellEditor", 
                params: {
                    maxLength: column.editableMaxLength
                }
            }
        }
        if (column.cellType == TableCellType.int || column.cellType == TableCellType.decimal) {
            return {
                component: "agNumberCellEditor", 
                params: {
                    precision: column.numDecimalDigits,
                    max: column.editableMaxLength != null ? Math.pow(10, column.editableMaxLength) - 1 : null
                }
            }
        }
        if (column.cellType == TableCellType.date) {
            return {
                component: "agDateCellEditor", 
                params: {
                }
            }
        }
        return null;
    }

    @Input()
    set selection(value: any) {
        this._selection = value;
    }
    
    selectFirstRow() {
        this.grid.api.getDisplayedRowAtIndex(0)?.setSelected(true);
    }

    ngDoCheck(_): void {
        if (this.grid != null && this.mustRefresh != null && this.mustRefresh()) {
            this.grid.api.refreshCells({ force: true});
        }
            
        if (this.fullRowEditForm != null) {
            for (let field of this.fullRowEditForm.fields) {
                this.columnToField(field, null);
            }
        }
        this.grid?.api?.setGridOption("headerHeight", this.headerHeight);
    }

    isFullScreenRowEditorColumnVisible(): boolean {
        return this._fullScreenRowEditor && this.app.isMobile();
    }

    async editRowFullScreen(rowNode: IRowNode): Promise<void> {
        this.fullRowEditForm = new FrontendFormDefinition(this.colDefs
            .filter(colDef => colDef.colId != SmTable2Component.COLUMN_ID_EDIT_ROW_FULL_SCREEN && colDef.colId != SmTable2Component.COLUMN_ID_REMOVE)
            .map(colDef => this.columnToField(null, colDef)));         
        this.fullRowEditRowNode = rowNode;

        try {
            await this.app.messageDialog.form(this.fullRowEditForm, "Zeile editieren", this.app.messageDialog.ok, this.app.isMobile());
        }
        finally {
            this.fullRowEditForm = null;
            this.fullRowEditRowNode = null;
        }
    }

    private columnToField(field: FrontendFieldDefinition, colDef: ColDef): FrontendFieldDefinition {
        if (colDef == null) {
            colDef = this.colDefs.find(colDef2 => colDef2.colId == field.id);
        }
        let col = this._data.columns.find(col => col.id == colDef.colId);
        if (field == null) {
            field = new FrontendFieldDefinition(colDef.colId, colDef.headerName, TableCellType.toFrontendFieldType((colDef.context.tableColumn as TableColumn).cellType), {
                onValueChanged: () => {
                    const params = {
                        data: this.fullRowEditRowNode.data,
                        newValue: field.value,
                        oldValue: this.fullRowEditRowNode.data[colDef.colId],
                        colDef: colDef,
                      //  column: column,
                        node: this.fullRowEditRowNode,
                        api: this.grid.api,
                      //  columnApi: this.gridColumnApi,
                        context: this.grid.context
                      };
                    (colDef.valueSetter as any)(params);
                    
                    this.cellValueChanged({ data: this.fullRowEditRowNode.data, colDef, node: this.fullRowEditRowNode });
                }
            });
            field.maxValue = col.cellType == TableCellType.int && col.editableMaxLength != null ? Math.pow(10, col.editableMaxLength) - 1 : null;
        }

        if (this.fullRowEditRowNode != null) {
            if (field.value == null) {
                field.value = this.grid.api.getCellValue({ rowNode: this.fullRowEditRowNode, colKey: colDef.colId});
            }
            field.enabled = this.isCellEditableInternal(this.fullRowEditRowNode, colDef.context.tableColumn);
            let editor = col.getEditor(() => new TableCellEvent(null, this.fullRowEditRowNode?.data as TableRow, colDef, this.fullRowEditRowNode?.rowIndex ?? -1, this.getColIndex(colDef.colId)));
            let isDropdown = editor != null && editor.type == FrontendFieldType.comboBox;
            field.listItems = isDropdown ? editor.listItems : null;
            field.dropdownEditable = false;
            field.type = isDropdown ? FrontendFieldType.comboBox : field.type;
        }
        return field;
    }

    isCellEditable(rowIndex: number, col: Column): boolean {
        const colDef = col.getColDef();
      
        if (colDef.editable === true) {
          return true;
        }
      
        if (typeof colDef.editable === 'function') {
          const rowNode = this.grid.api.getDisplayedRowAtIndex(rowIndex);
          if (!rowNode) {
            return false;
          }
      
          const params = {
            node: rowNode,
            data: rowNode.data,
            rowIndex: rowIndex,
            colDef: colDef,
            column: col,
            api: this.grid.api,
            context: this.context
          };
      
          return colDef.editable(params);
        }
      
        return false;
    }

    getFirstEditableColId(rowIndex: number): string | null {
        const columns = this.grid.api.getAllDisplayedColumns();
    
        for (let i = 0; i < columns.length; i++) {
            const col = columns[i];
            const colDef = col.getColDef();
    
            const isNavigable = !colDef.suppressNavigable;
            const isEditable = this.isCellEditable(rowIndex, col);
    
            if (isNavigable && isEditable) {
                return col.getColId();
            }
        }
    
        return null;
    }

    getLastEditableColId(rowIndex: number): string | null {
        const columns = this.grid.api.getAllDisplayedColumns();
    
        for (let i = columns.length - 1; i >= 0; i--) {
            const col = columns[i];
            const colDef = col.getColDef();
    
            const isNavigable = !colDef.suppressNavigable;
            const isEditable = this.isCellEditable(rowIndex, col);
    
            if (isNavigable && isEditable) {
                return col.getColId();
            }
        }
    
        return null;
    }

    cellKeyDown(event: CellKeyDownEvent<any, any> | any): void {
        if (this.addRowOnTabInLastCell && (event.event as any).key === 'Tab') {
            const lastRowIndex = this.grid.api.getDisplayedRowCount() - 1;
            const lastColIndex = this.colDefs.length - 1;
        
            if (event.rowIndex === lastRowIndex && event.column.colId == this.getLastEditableColId(event.rowIndex)) {
                event. event.preventDefault(); 
                this.addRow();
            
                setTimeout(() => {
                    this.grid.api.startEditingCell({
                    rowIndex: lastRowIndex + 1,
                    colKey: this.getFirstEditableColId(lastRowIndex)
                    });
                });
            }
        }
    }

      /*  let params = paramsx as any as CellKeyDownEvent;
        if ((params.9event as any as KeyboardEvent).key === 'ArrowUp') {
            this.grid.api.stopEditing(); 
      
            const nextRowIndex = params.node.rowIndex - 1;
            if (nextRowIndex >= 0) {
              params.api.setFocusedCell(nextRowIndex, params.column.getColId());
              params.api.startEditingCell({
                rowIndex: nextRowIndex,
                colKey: params.column.getColId(),
              });
            }
        }
        else if ((params.event as any as KeyboardEvent).key === 'ArrowDown') {
            this.grid.api.stopEditing(); 
        
            const nextRowIndex = params.node.rowIndex + 1;
            if (nextRowIndex < params.api.getDisplayedRowCount()) {
                params.api.setFocusedCell(nextRowIndex, params.column.getColId());
                params.api.startEditingCell({
                rowIndex: nextRowIndex,
                colKey: params.column.getColId(),
                });
            }
        }*/
    
    cellMouseOver(event: any): void {
        event.node.hover = true;
        event.api.refreshCells({ rowNodes: [event.node], force: true, suppressFlash: true });
    }


    cellMouseOut(event: any): void {
        event.node.hover = false;
        event.api.refreshCells({ rowNodes: [event.node], force: true, suppressFlash: true });
    }

    cellClicked(event: any): void {
        if (this.editOnClick) {
            this.grid.api.startEditingCell({
                rowIndex: event.rowIndex,
                colKey: event.column.getId()
            });
        }
    }

    cellEditingStarted(event: any): void {
        if (this.onCellEditingStarted.observed) {
            let row = event.data as TableRow;
            let col = event.colDef as ColDef;
            this.onCellEditingStarted.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.node.rowIndex, this.getColIndex(col.colId)));         
        }
    }

    cellEditingStopped(event: any): void {
        if (this.onCellEditingStopped.observed) {
            let row = event.data as TableRow;
            let col = event.colDef as ColDef;
            this.onCellEditingStopped.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.node.rowIndex, this.getColIndex(col.colId)));         
        }
    }

    cellFocused(event: any): void {
        if (this.onCellFocused.observed) {
            let row = event.data as TableRow;
            let col = event.colDef as ColDef;
            this.onCellFocused.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.node.rowIndex, this.getColIndex(col.colId)));         
        }
    }

    getRowClass(params: any): string {
        return (params.data as TableRow)?.styleClass;
    }

    rowDoubleClicked(event: any): void {
        this.onRowDoubleClicked.emit(event.data as TableRow);
    }

    rowSelected(event: any): void {
        if (event.node.isSelected()) {
            this.onRowSelect.emit(event.data as TableRow);
        }
    }

    rowDragEnd(event: RowDragEndEvent) {
        const newOrder: any[] = [];
        this.grid.api.forEachNode((node) => {
          newOrder.push(node.data); 
        });
        this._data.rows = newOrder; 
        if (this.performSetTableRows != null) {
            this.performSetTableRows(newOrder);
        }
    }

    public getRowId: GetRowIdFunc = (params: GetRowIdParams) => (params.data as TableRow).id;

    selectionChanged(event: any): void {
        this._selection = event.api.getSelectedRows().map(r => r as TableRow);
        this.selectionChange.emit(this._selection);
    }

    
    setSelectedRowIndex(index: number): void {
        this.grid.api.getDisplayedRowAtIndex(index)?.setSelected(true);
    }

    getColIndex(id: string): number {
        return this.colDefs.findIndex(colDef => colDef.colId == id);
    }

    getColDefAndIndex(id: string): {def: ColDef, index: number} {
        let i = 0;
        for (let def of this.colDefs) {
            if (def.colId == id) {
                return { def, index: i };
            }
            i++;
        }
        return null;
    }

    cellValueChanged(event: any): void {
        let row = event.data as TableRow;
        let col = event.colDef as ColDef;
        this.onCellValueChanged.emit(new TableCellEvent(TableCell.getValue(row.values[col.colId]), row, col, event.node.rowIndex, this.getColIndex(col.colId)));     
    }

    sortChanged(event: any): void {
        this.sortCache.clear();
    }

    getUnfilteredRows(): TableRow[] {
        let result: TableRow[] = [];
        this.grid.api.forEachNodeAfterFilterAndSort((node) => {
            result.push(node.data);
        });

        return result;
    }

    exportCsv(): void {
        GuiUtils.downloadBytesAsFile(GuiUtils.stringAsciiToArrayBuffer(this._data.toCsv(this.getUnfilteredRows())), "Tabelle.csv");
    }

    async exportXlsx(): Promise<void> {
        let csv = this._data.toCsv(this.getUnfilteredRows());
        let columnState = this.grid.api.getColumnState();
        let xlsx = await SmTable2Component.convertCsvToExcel(csv, Utils.arrayMapWithIndex(this._data.columns, (col, index) => Utils.fromPlain(ExcelColumn, {
            dataType: TableCellType.toGenericDataType(col.cellType),
            width: columnState.find(cs => cs.colId == col.id)?.width * 0.15
        })));
        GuiUtils.downloadBytesAsFile(GuiUtils.base64ToArrayBuffer(xlsx), "Tabelle.xlsx");
    }

    asciiTableToClipboard(): void {
        GuiUtils.copyToClipboard(this._data.toAsciiTable(this.getUnfilteredRows()));
    }

    toggleFilterRow(): void {
        if (!this.allowFilter) {
            return;
        }
        this.floatingFilter = !this.floatingFilter;
        for (let col of this.colDefs) {
            col.floatingFilter = this.floatingFilter;
        }
        this.grid.api.setGridOption("columnDefs", this.colDefs);
    }

    setFilter(columnId: string, value: any): void {
        if (!this.allowFilter) {
            return;
        }
        this.grid.api.setFilterModel({
            [columnId]: {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });
        this.grid.api.onFilterChanged();
    }

    openSettings(event: any): void {
        let unfilteredRows = this.getUnfilteredRows();
        this.app.openContextMenu([
            this.allowFilter ? {
                label: this.app.t("table.toggleFilterRow"),
                command: this.toggleFilterRow.bind(this)
            } : null,
            {
                label: this.app.t("table.exportCsv"),
                command: this.exportCsv.bind(this)
            },
            SmTable2Component.convertCsvToExcel != null ? {
                label: this.app.t("table.exportXlsx"),
                command: this.exportXlsx.bind(this)
            } : null,
            {
                label: this.app.t("table.asciiTableToClipboard"),
                command: this.asciiTableToClipboard.bind(this)
            },
            {
                separator: true
            },
            {
                label: "Anzahl Zeilen: " + this._data.rows.length,
                disabled: true
            },
            unfilteredRows.length != this._data.rows.length ? {
                label: "Anzahl sichtbarer Zeilen: " + unfilteredRows.length,
                disabled: true
            } : null           
        ]);
        event.stopPropagation();
    }

    addRow(): void {
        if (this.performAddTableRow != null) {
            this.performAddTableRow();
        }
        GuiUtils.angularTimer(() => this.grid.api.ensureIndexVisible(this._data.rows.length - 1, 'bottom'));
    }

    async removeRow(row: TableRow, showWarningMessage = false): Promise<void> {
        if (showWarningMessage && this.removeRowWarningMessage != null && !await this.app.messageDialog.yesNo(this.removeRowWarningMessage, this.app.t("general.warning"))) {
            return;
        }
        if (this.performRemoveTableRow != null) {
            let index = this._data.rows.findIndex(row2 => row2 == row);
            if (index >= 0) {
                this.performRemoveTableRow(row, index);
            }
        }
    }

    tabToNextCell(params: any): boolean | CellPosition {
        const nextRowIndex =  params.backwards 
          ? params.previousCellPosition.rowIndex - 1 // Rückwärts springen
          : params.previousCellPosition.rowIndex + 1; // Vorwärts springen
    
        // Verhindere das Springen über die Tabellenränder hinaus
        if (nextRowIndex < 0 || nextRowIndex >= this.grid.api.getDisplayedRowCount()) {
          return null; // Stoppe das Springen
        }
    
        // Gebe die nächste Zelle in derselben Spalte zurück
        return {
          rowIndex: nextRowIndex,
          column: params.previousCellPosition.column,
          rowPinned: params.previousCellPosition.rowPinned,
        };
    }

}

